import { type RefObject, useEffect, useRef } from 'react';
import { type Point, clampScale, distance } from './panZoomMath';

/**
 * Block 14 «Раскрой» — pinch-zoom таблицы Деталей через размер ячеек (PR #8b,
 * ADR-0071, реализует ADR-0069 D4). Кастомный Pointer Events-слой (без либ).
 *
 * **Модель (Excel):** freeze-header — нативный `position:sticky` thead; pan +
 * vertical/horizontal скролл — НАТИВНЫЙ (`.sc-table-wrap { overflow:auto }`);
 * tap по ячейке — нативный (браузер сам отличает тап от скролла). Этот хук
 * добавляет ТОЛЬКО pinch (2 пальца): меняет CSS-переменную `--cell-scale` на
 * `.sc-table`, из-за чего font-size/padding ячеек масштабируются равномерно
 * (шапка+тело, столбцы выровнены — одна таблица). Диапазон 0.5×–3×.
 *
 * **Почему size-zoom, а не `transform: scale`:** transform на `.sc-table`
 * (предке sticky-`th`) ломает `position:sticky` (containing-block смещается) →
 * шапка уезжает при скролле. Size-zoom этого конфликта не имеет (ADR-0071).
 *
 * **Не вмешивается в 1 палец:** при <2 активных pointers хук ничего не делает и
 * НЕ зовёт `preventDefault` → нативный скролл и тап-редактирование работают.
 * При 2 pointers — `preventDefault` (passive:false) гасит браузерный pinch/
 * двупальцевый скролл и применяет наш зум.
 *
 * Активен ТОЛЬКО на мобильном (`enabled`, обычно `matchMedia('(max-width:480px)')`).
 * На десктопе инертен и сбрасывает `--cell-scale` (десктоп — обычная таблица).
 */
export interface UsePinchZoomOptions {
  /** Вьюпорт (`.sc-table-wrap`) — слушатель pointer-событий. */
  viewportRef: RefObject<HTMLElement | null>;
  /** Контент (`.sc-table`) — на него пишется `--cell-scale`. */
  contentRef: RefObject<HTMLElement | null>;
  /** Активен ли pinch-zoom (мобильный). На false хук инертен + сброс scale. */
  enabled: boolean;
}

const CELL_SCALE_VAR = '--cell-scale';

export function usePinchZoom({ viewportRef, contentRef, enabled }: UsePinchZoomOptions): void {
  // Live-состояние жеста в ref'ах — без ре-рендера на кадр.
  const scaleRef = useRef<number>(1);
  const pointersRef = useRef<Map<number, Point>>(new Map());
  const startDistRef = useRef<number>(0);
  const startScaleRef = useRef<number>(1);
  const rafRef = useRef<number | null>(null);

  useEffect(() => {
    const viewport = viewportRef.current;
    const content = contentRef.current;
    if (!viewport || !content) return;

    // Десктоп / выключено — сброс масштаба (обычная таблица).
    if (!enabled) {
      content.style.removeProperty(CELL_SCALE_VAR);
      scaleRef.current = 1;
      return;
    }

    const pointers = pointersRef.current;

    const writeScale = () => {
      rafRef.current = null;
      content.style.setProperty(CELL_SCALE_VAR, String(scaleRef.current));
    };

    const scheduleWrite = () => {
      if (rafRef.current != null) return;
      const raf =
        typeof window.requestAnimationFrame === 'function'
          ? window.requestAnimationFrame
          : (cb: FrameRequestCallback) => window.setTimeout(() => cb(performance.now()), 16);
      rafRef.current = raf(writeScale);
    };

    const onPointerDown = (e: PointerEvent) => {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        const pts = [...pointers.values()];
        startDistRef.current = distance(pts[0]!, pts[1]!);
        startScaleRef.current = scaleRef.current;
      }
    };

    const onPointerMove = (e: PointerEvent) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      // Pinch только при двух пальцах. Один палец — нативный скролл/тап, не трогаем.
      if (pointers.size < 2) return;
      const pts = [...pointers.values()];
      const curDist = distance(pts[0]!, pts[1]!);
      if (startDistRef.current > 0) {
        scaleRef.current = clampScale(startScaleRef.current * (curDist / startDistRef.current));
      }
      e.preventDefault(); // гасим браузерный pinch / двупальцевый скролл
      scheduleWrite();
    };

    const endPointer = (e: PointerEvent) => {
      pointers.delete(e.pointerId);
      // Если остался 1 палец — pinch завершён; дальнейшее движение = нативный скролл.
    };

    viewport.addEventListener('pointerdown', onPointerDown);
    viewport.addEventListener('pointermove', onPointerMove, { passive: false });
    viewport.addEventListener('pointerup', endPointer);
    viewport.addEventListener('pointercancel', endPointer);

    return () => {
      viewport.removeEventListener('pointerdown', onPointerDown);
      viewport.removeEventListener('pointermove', onPointerMove);
      viewport.removeEventListener('pointerup', endPointer);
      viewport.removeEventListener('pointercancel', endPointer);
      if (rafRef.current != null && typeof window.cancelAnimationFrame === 'function') {
        window.cancelAnimationFrame(rafRef.current);
      }
      rafRef.current = null;
      pointers.clear();
    };
  }, [enabled, viewportRef, contentRef]);
}
